import { registerFunctionalComponent } from 'js/lib/components/component'
import { normalizedPhoneNumber } from './normalizer'
import 'flag-icons/css/flag-icons.min.css'

registerFunctionalComponent('.js-c-form-input--phone', 'PhoneInput', PhoneInput)

function PhoneInput(component) {
  const root = component.element
  const hiddenInput = root.querySelector('.js-c-form-input-phone-hidden')
  const numberInput = root.querySelector('.js-c-form-input-phone-number')
  const countryButton = root.querySelector('.js-c-form-input-phone-country-button')
  const dropdown = root.querySelector('.js-c-form-input-phone-dropdown')
  const searchInput = root.querySelector('.js-c-form-input-phone-country-search')
  const countryOptions = Array.from(root.querySelectorAll('.js-c-form-input-phone-country-option'))
  const selectedFlag = root.querySelector('.js-c-form-input-phone-selected-flag')
  const selectedDialCode = root.querySelector('.js-c-form-input-phone-selected-dial-code')
  let numberChanged = false

  if (!hiddenInput || !numberInput || !countryButton || !dropdown || !searchInput || !selectedFlag || !selectedDialCode) return

  countryButton.setAttribute('aria-expanded', 'false')

  component.useHandler(() => {
    const handleRootClick = event => {
      const option = event.target.closest('.js-c-form-input-phone-country-option')

      if (option) {
        selectCountry(option)
        closeDropdown()
        numberInput.focus()
        return
      }

      if (event.target.closest('.js-c-form-input-phone-country-button')) {
        toggleDropdown()
      }
    }

    const handleDocumentClick = event => {
      if (!root.contains(event.target)) closeDropdown()
    }

    const handleSearchInput = () => filterCountries(searchInput.value)
    const handlePhoneInput = () => {
      numberChanged = true
      updatePhoneFields()
    }
    const handleSubmit = () => {
      if (numberChanged) updatePhoneFields()
    }
    const handleKeydown = event => handleDropdownKeydown(event)

    root.addEventListener('click', handleRootClick)
    document.addEventListener('click', handleDocumentClick)
    searchInput?.addEventListener('input', handleSearchInput)
    numberInput.addEventListener('input', handlePhoneInput)
    numberInput.addEventListener('change', handlePhoneInput)
    numberInput.form?.addEventListener('submit', handleSubmit, true)
    root.addEventListener('keydown', handleKeydown)

    return () => {
      root.removeEventListener('click', handleRootClick)
      document.removeEventListener('click', handleDocumentClick)
      searchInput?.removeEventListener('input', handleSearchInput)
      numberInput.removeEventListener('input', handlePhoneInput)
      numberInput.removeEventListener('change', handlePhoneInput)
      numberInput.form?.removeEventListener('submit', handleSubmit, true)
      root.removeEventListener('keydown', handleKeydown)
    }
  })

  hiddenInput.disabled = numberInput.disabled

  function toggleDropdown() {
    if (dropdown.classList.contains('tw-hidden')) {
      openDropdown()
    } else {
      closeDropdown()
    }
  }

  function openDropdown() {
    dropdown.classList.remove('tw-hidden')
    countryButton.setAttribute('aria-expanded', 'true')
    searchInput.value = ''
    filterCountries('')
    searchInput.focus()
  }

  function closeDropdown() {
    dropdown.classList.add('tw-hidden')
    countryButton.setAttribute('aria-expanded', 'false')
  }

  function filterCountries(term) {
    const normalizedTerm = term.trim().toLowerCase()

    countryOptions.forEach(option => {
      const matches = option.dataset.searchText.toLowerCase().includes(normalizedTerm)
      option.classList.toggle('tw-hidden', !matches)
    })
  }

  function visibleCountryOptions() {
    return countryOptions.filter(option => !option.classList.contains('tw-hidden'))
  }

  function selectCountry(option) {
    numberChanged = true
    countryButton.dataset.countryCode = option.dataset.countryCode
    countryButton.dataset.countryName = option.dataset.countryName
    countryButton.dataset.dialCode = option.dataset.dialCode
    countryButton.dataset.nationalPrefix = option.dataset.nationalPrefix || ''
    selectedFlag.className = selectedFlagClasses(option.dataset.flagClass)
    selectedDialCode.textContent = `+${option.dataset.dialCode}`
    countryButton.setAttribute('aria-label', countryButtonLabel(option.dataset.countryName, option.dataset.dialCode))
    updateSelectedOption(option)
    updatePhoneFields()
  }

  function selectedFlagClasses(flagClass) {
    return `js-c-form-input-phone-selected-flag tw-inline-block !tw-h-5 !tw-w-7 tw-shrink-0 !tw-bg-cover !tw-bg-center ${flagClass}`
  }

  function countryButtonLabel(countryName, dialCode) {
    return `Country calling code, ${countryName} +${dialCode}`
  }

  function updateSelectedOption(selectedOption) {
    countryOptions.forEach(option => {
      option.setAttribute('aria-selected', option === selectedOption ? 'true' : 'false')
    })
  }

  function handleDropdownKeydown(event) {
    if (!shouldHandleDropdownKeydown(event)) return

    if (event.key === 'Escape') {
      closeDropdown()
      countryButton.focus()
      return
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (dropdown.classList.contains('tw-hidden')) openDropdown()
      focusCountryOption(event.key === 'ArrowDown' ? 1 : -1, event.target)
      return
    }

    if (event.key !== 'Enter') return

    const option = event.target.closest('.js-c-form-input-phone-country-option')
    if (!option) return

    event.preventDefault()
    selectCountry(option)
    closeDropdown()
    numberInput.focus()
  }

  function shouldHandleDropdownKeydown(event) {
    return Boolean(
      event.target.closest?.('.js-c-form-input-phone-country-button') ||
      event.target.closest?.('.js-c-form-input-phone-dropdown')
    )
  }

  function focusCountryOption(direction, currentTarget) {
    const options = visibleCountryOptions()
    if (!options.length) return

    const currentOption = currentTarget.closest?.('.js-c-form-input-phone-country-option')
    const currentIndex = options.indexOf(currentOption)
    const fallbackIndex = direction > 0 ? 0 : options.length - 1
    const nextIndex = currentIndex === -1 ? fallbackIndex : (currentIndex + direction + options.length) % options.length

    options[nextIndex].focus()
  }

  function updatePhoneFields() {
    const normalizedNumber = normalizedPhoneNumber(numberInput.value, countryButton.dataset)
    hiddenInput.value = normalizedNumber
    hiddenInput.disabled = numberInput.disabled
  }
}
